import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { GetConsolidatedContacts } from '../../graphql/company';
import { isValidCollectionEmail } from '../../utils/collectionEmailContacts';
import './NotificationModal.styles.scss';

const EmailDropdown = ({
  value = [],
  onChange,
  placeholder,
  className = "",
  disabled = false,
  id,
  companyId,
  menuPortal = false,
  ...props
}) => {
  const { t } = useTranslation('repository');
  const resolvedPlaceholder = placeholder ?? t('emailDropdown.placeholder');
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState(value || []);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const suppressFocusRef = useRef(false);
  const [menuRect, setMenuRect] = useState(null);

  const sameEmail = (left, right) =>
    String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase();

  // Fetch consolidated contacts from API
  const { data: contactsData, fetching: contactsFetching, error: contactsError, reexecuteQuery } = GetConsolidatedContacts(companyId);

  // Debug logging
  useEffect(() => {
    if (!companyId) {
      console.warn('EmailDropdown: No companyId provided, companyId:', companyId);
    }
  }, [companyId]);

  useEffect(() => {
    if (contactsError) {
      console.error('EmailDropdown: Error fetching contacts:', contactsError);
    }
  }, [contactsError]);

  // Re-execute query when dropdown opens and companyId is available
  useEffect(() => {
    if (isOpen && companyId) {
      // Small delay to ensure the query hook has processed the companyId
      const timer = setTimeout(() => {
        reexecuteQuery({ requestPolicy: 'network-only' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, companyId, reexecuteQuery]);

  // Transform API data to email options format
  const emailOptions = useMemo(() => {
    if (!contactsData?.getConsolidatedContacts) return [];

    return contactsData.getConsolidatedContacts
      .filter((contact) => contact.email && isValidCollectionEmail(contact.email))
      .map((contact, index) => ({
      id: `contact-${index}-${contact.email}`,
      email: contact.email,
      name: contact.name,
      phone: contact.phone,
      types: contact.types || []
    }));
  }, [contactsData]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return emailOptions;
    const query = searchQuery.toLowerCase();
    return emailOptions.filter(option =>
      option.email.toLowerCase().includes(query) ||
      (option.name && option.name.toLowerCase().includes(query)) ||
      (option.types && option.types.some(type => type.toLowerCase().includes(query)))
    );
  }, [searchQuery, emailOptions]);

  // Update selectedEmails when value prop changes
  useEffect(() => {
    setSelectedEmails(value || []);
  }, [value]);

  // Close on an outside press. The label and the field can take focus in the
  // same gesture and would reopen the menu, so that focus is ignored.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const inTrigger = dropdownRef.current?.contains(target) || path.includes(dropdownRef.current);
      const inMenu = menuRef.current?.contains(target) || path.includes(menuRef.current);
      if (inTrigger || inMenu) return;

      suppressFocusRef.current = true;
      setIsOpen(false);
      setSearchQuery('');
      inputRef.current?.blur();

      // The label focuses this input on click, which is after pointerup.
      // Keep the guard until that gesture finishes.
      const fallback = window.setTimeout(() => {
        suppressFocusRef.current = false;
      }, 400);
      const finishGesture = () => {
        window.clearTimeout(fallback);
        window.setTimeout(() => {
          suppressFocusRef.current = false;
        }, 0);
      };
      document.addEventListener('pointerup', finishGesture, { capture: true, once: true });
      document.addEventListener('pointercancel', finishGesture, { capture: true, once: true });
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isOpen]);

  // Handle option selection
  const handleSelect = (option) => {
    const isSelected = selectedEmails.some((email) => sameEmail(email.email, option.email));
    let newSelectedEmails;

    if (isSelected) {
      newSelectedEmails = selectedEmails.filter((email) => !sameEmail(email.email, option.email));
    } else {
      // Add if not selected
      newSelectedEmails = [...selectedEmails, option];
    }

    setSelectedEmails(newSelectedEmails);
    setSearchQuery('');

    if (onChange) {
      onChange(newSelectedEmails);
    }
  };

  // Handle input change for search
  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (!isOpen && !suppressFocusRef.current) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (suppressFocusRef.current) {
      inputRef.current?.blur();
      return;
    }
    setIsOpen(true);
  };

  // Handle key navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
      inputRef.current?.blur();
    } else if (e.key === 'Enter' && filteredOptions.length > 0 && !isOpen) {
      e.preventDefault();
      handleSelect(filteredOptions[0]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  };

  // Handle removing selected email
  const handleRemoveEmail = (emailId, e) => {
    e.stopPropagation();
    const newSelectedEmails = selectedEmails.filter(email => email.id !== emailId);
    setSelectedEmails(newSelectedEmails);

    if (onChange) {
      onChange(newSelectedEmails);
    }
  };

  useLayoutEffect(() => {
    if (!menuPortal || !isOpen || !dropdownRef.current) return undefined;
    const updateRect = () => {
      const rect = dropdownRef.current.getBoundingClientRect();
      setMenuRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [menuPortal, isOpen]);

  const displayValue = isOpen ? searchQuery : '';
  const showPlaceholder = !selectedEmails.length && !isOpen;

  const menuBody = contactsFetching ? (
    <div className="repositorio-email-dropdown-no-results">
      <div className="repositorio-email-no-results-message">
        Cargando contrapartes...
      </div>
    </div>
  ) : filteredOptions.length > 0 ? (
    <ul className="repositorio-email-dropdown-options" role="listbox">
      {filteredOptions.map((option) => {
        const isSelected = selectedEmails.some((email) => sameEmail(email.email, option.email));
        return (
          <li
            key={option.id}
            className={`repositorio-email-dropdown-option ${isSelected ? 'selected' : ''}`}
            onClick={() => handleSelect(option)}
            role="option"
            aria-selected={isSelected}
          >
            <span className="repositorio-email-option-checkbox">
              {isSelected && <FeatherIcon icon="check" size={14} />}
            </span>
            <span className="repositorio-email-option-text">
              <span className="repositorio-email-option-email">{option.email}</span>
              {option.types && option.types.length > 0 && (
                <span className="repositorio-email-option-types">
                  {option.types.join(', ')}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  ) : (
    <div className="repositorio-email-dropdown-no-results">
      <div className="repositorio-email-no-results-message">
        {searchQuery.trim() ? t('emailDropdown.noResults') : t('emailDropdown.noCounterparties')}
      </div>
    </div>
  );

  const menuNode = menuPortal && menuRect ? (
    <div
      ref={menuRef}
      className="repositorio-email-dropdown repositorio-email-dropdown--portal open"
      style={{
        top: menuRect.top,
        left: menuRect.left,
        width: menuRect.width,
      }}
    >
      <div className="repositorio-email-dropdown-menu">
        {menuBody}
      </div>
    </div>
  ) : (
    <div ref={menuRef} className="repositorio-email-dropdown-menu">
      {menuBody}
    </div>
  );

  return (
    <div
      ref={dropdownRef}
      className={`repositorio-email-dropdown ${className} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
      {...props}
    >
      <div
        className="repositorio-email-dropdown-wrapper"
        onClick={() => {
          if (disabled || isOpen || suppressFocusRef.current) return;
          if (selectedEmails.length > 0) {
            setIsOpen(true);
            requestAnimationFrame(() => inputRef.current?.focus());
          }
        }}
      >
        {/* Selected emails chips */}
        {selectedEmails.length > 0 && !isOpen && (
          <div className="repositorio-email-selected-chips">
            {selectedEmails.map((email) => (
              <span key={email.id} className="repositorio-email-chip">
                <span className="repositorio-email-chip-text">{email.email}</span>
                <button
                  type="button"
                  className="repositorio-email-chip-remove"
                  onClick={(e) => handleRemoveEmail(email.id, e)}
                  disabled={disabled}
                >
                  <FeatherIcon icon="x" size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        <input
          ref={inputRef}
          id={id}
          type="text"
          className="repositorio-email-dropdown-input"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={showPlaceholder ? resolvedPlaceholder : ''}
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
        />
        <span className="repositorio-email-dropdown-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path stroke="#6B7280" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4"/>
          </svg>
        </span>
      </div>
      {isOpen && !disabled && (!menuPortal || menuRect)
        ? (menuPortal ? createPortal(menuNode, document.body) : menuNode)
        : null}
    </div>
  );
};

export default EmailDropdown;